import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { WizardStep, CampaignWizardFormData, WizardCreative } from '../types/campaigns.types';
import { WIZARD_STEPS } from '../types/campaigns.types';

interface CampaignWizardState {
  currentStep: WizardStep;
  formData: Partial<CampaignWizardFormData>;
  isSubmitting: boolean;
  /** Track which steps have been completed/validated */
  completedSteps: WizardStep[];
  /** Campaign ID when editing a draft */
  draftCampaignId: string | null;
}

interface CampaignWizardActions {
  setStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateFormData: (data: Partial<CampaignWizardFormData>) => void;
  setSubmitting: (submitting: boolean) => void;
  markStepComplete: (step: WizardStep) => void;
  setDraftCampaignId: (id: string | null) => void;
  reset: () => void;
}

type CampaignWizardStore = CampaignWizardState & CampaignWizardActions;

/**
 * Merges pre-pool drafts (multiple "Creative N" entries, each with its own
 * headlines/descriptions/images) into the single pooled creative the wizard
 * edits today. Platforms mix pooled assets freely, so concat + dedupe loses
 * nothing; type/CTA/video/carousel fields come from the first creative.
 */
function mergeCreativesIntoPool(creatives: WizardCreative[]): WizardCreative[] {
  if (creatives.length <= 1) return creatives;
  const [first] = creatives;
  if (!first) return creatives;
  const dedupeStrings = (values: string[]) => [...new Set(values.filter(Boolean))];
  const dedupeImages = <T extends { imageStorageKey?: string }>(images: T[]) => {
    const seen = new Set<string>();
    return images.filter((img) => {
      if (!img.imageStorageKey || seen.has(img.imageStorageKey)) return false;
      seen.add(img.imageStorageKey);
      return true;
    });
  };
  return [
    {
      ...first,
      headlines: dedupeStrings(creatives.flatMap((c) => c.headlines ?? [])),
      descriptions: dedupeStrings(creatives.flatMap((c) => c.descriptions ?? [])),
      // File objects/blob previews never survive persistence — drop them.
      mediaFiles: [],
      mediaPreviews: [],
      generatedImages: dedupeImages(creatives.flatMap((c) => c.generatedImages ?? [])),
      squareMediaFiles: [],
      squareMediaPreviews: [],
      squareGeneratedImages: dedupeImages(creatives.flatMap((c) => c.squareGeneratedImages ?? [])),
    },
  ];
}

/**
 * Releases the local object URLs held in the draft. The store owns them:
 * CreativeUploader only mints them and unmounts on every step change, so
 * revoking there would kill previews the wizard still displays. Discarding the
 * draft is the one moment they are genuinely dead.
 */
function revokeDraftPreviewUrls(creatives: WizardCreative[] | undefined): void {
  const revoke = (url: string | undefined) => {
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };
  for (const creative of creatives ?? []) {
    creative.mediaPreviews?.forEach(revoke);
    creative.squareMediaPreviews?.forEach(revoke);
    revoke(creative.videoPreview);
    revoke(creative.videoThumbnailPreview);
  }
}

const initialState: CampaignWizardState = {
  currentStep: 'objective-budget',
  formData: {},
  isSubmitting: false,
  completedSteps: [],
  draftCampaignId: null,
};

export const useCampaignWizardStore = create<CampaignWizardStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setStep: (step) => set({ currentStep: step }),

      nextStep: () => {
        const { currentStep } = get();
        const currentIndex = WIZARD_STEPS.indexOf(currentStep);
        const nextStep = WIZARD_STEPS[currentIndex + 1];
        if (nextStep) {
          set({ currentStep: nextStep });
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        const currentIndex = WIZARD_STEPS.indexOf(currentStep);
        const prevStep = WIZARD_STEPS[currentIndex - 1];
        if (prevStep) {
          set({ currentStep: prevStep });
        }
      },

      updateFormData: (data) =>
        set((state) => ({
          formData: { ...state.formData, ...data },
        })),

      setSubmitting: (submitting) => set({ isSubmitting: submitting }),

      markStepComplete: (step) =>
        set((state) => ({
          completedSteps: state.completedSteps.includes(step)
            ? state.completedSteps
            : [...state.completedSteps, step],
        })),

      setDraftCampaignId: (id) => set({ draftCampaignId: id }),

      reset: () => {
        revokeDraftPreviewUrls(get().formData.creatives);
        set(initialState);
      },
    }),
    {
      name: 'promote-campaign-wizard',
      storage: createJSONStorage(() => sessionStorage),
      version: 1,
      // v0 drafts carried multiple "Creative N" entries; the pool model edits a
      // single creative, so merge them on rehydration.
      migrate: (persisted, version) => {
        const state = persisted as Partial<CampaignWizardState>;
        if (version === 0 && state.formData?.creatives?.length) {
          return {
            ...state,
            formData: {
              ...state.formData,
              creatives: mergeCreativesIntoPool(state.formData.creatives),
            },
          };
        }
        return state;
      },
      partialize: (state) => ({
        currentStep: state.currentStep,
        // Raw File objects and blob: preview URLs don't survive a sessionStorage
        // round-trip (a File serializes to `{}`, blob URLs are revoked on
        // reload), so strip them from the persisted draft. Uploaded creatives
        // still survive via generatedImages (S3 storage keys). Without this, a
        // rehydrated draft feeds `{}` "files" to CreativeUploader and crashes it.
        formData: {
          ...state.formData,
          creatives: state.formData.creatives?.map((creative) => ({
            ...creative,
            mediaFiles: [],
            mediaPreviews: [],
          })),
        },
        completedSteps: state.completedSteps,
        draftCampaignId: state.draftCampaignId,
      }),
    },
  ),
);
